import React, { useState, useCallback, useMemo, useRef } from 'react';
import { CommentThreadType } from '@/types';
import theme from './CommentsFilterable.module.scss';
import { Trans } from '@lingui/react/macro';
import { faChevronDown, faEye, faEyeSlash, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import CreateCommentThreadModal from '@/components/CreateCommentThreadModal/CreateCommentThreadModal';

import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams, useHistory } from 'react-router';
import { toNullableNumber } from '@/utils/html';
import { useParamReplace, useIsMobile } from '@/utils/hooks';
import { SendResidentialUnitMessageModal } from './SendResidentialUnitMessageModal';
import { ProjectPermissionToggle } from '@/components/permissions';
import {
    CommentThreadListSearchOptions,
    CommentThreadListSearchable,
    CommentThreadListSearchableApi,
    NoThreadPlaceholder,
} from '@ygtechnologies/bygr-frontend-chat';
import { Button, ButtonBar, Dropdown, DropdownButton, SlideToggle, Tabs } from '@ygtechnologies/rubus-ui';
import { CommentThreadWrapper } from '@/components/Comments/CommentThreadWrapper';

const internalMessageThreads = [
    CommentThreadType.ResidentialUnit,
    CommentThreadType.ChangeRequest,
    CommentThreadType.ExternalAgreement,
    CommentThreadType.Project,
];

interface CommentsFilterableProps {
    profileId: number;
    projectId: number;
    residentialUnitId?: number;
}

type FollowFilterType = 'All' | 'Followed' | 'NotFollowed';

const CommentsFilterable: React.FC<CommentsFilterableProps> = ({ profileId, projectId, residentialUnitId }) => {
    const [showCreateThreadModal, setShowCreateThreadModal] = useState(false);
    const [followFilter, setFollowFilter] = useState<FollowFilterType>('All');
    const [threadTypeFilter, setThreadTypeFilter] = useState<'All' | CommentThreadType>('All');
    const [threadVisibilityFilter, setThreadVisibilityFilter] = useState<'All' | 'Buyer' | 'Internal'>('All');
    const [showOnlyFlagged, setShowOnlyFlagged] = useState(false);

    const [sendMultiMessageModalIsOpen, setSendMultiMessageModalIsOpen] = useState(false);
    const isMobile = useIsMobile();

    const projectPermissions = useSelector((state: IState) => state.global.activeProject?.permissions);

    const [followFilterDropdownIsOpen, setFollowFilterDropdownIsOpen] = useState(false);
    const [typeFilterDropdownIsOpen, setTypeFilterDropdownIsOpen] = useState(false);

    const { id: threadId } = useParams<{ id: string | undefined }>();
    const replaceParams = useParamReplace<{ id: string | undefined }>();
    const selectedThreadId = toNullableNumber(threadId || '');
    const threadListRef = useRef<CommentThreadListSearchableApi>(null);

    const history = useHistory();
    const setSelectedThread = useCallback(
        (threadId: number | null) => {
            const url = replaceParams({ id: threadId?.toString() ?? undefined });
            history.replace(url);
        },
        [history, replaceParams]
    );

    const handleNewThreadCreated = async (threadId: number) => {
        if (threadListRef.current) {
            await threadListRef.current.reload();
        }

        setSelectedThread(threadId);
    };
    const handleSendNewMultiMessageClick = !residentialUnitId
        ? () => {
              setSendMultiMessageModalIsOpen(true);
          }
        : undefined;

    const canCreateThreads =
        !!residentialUnitId &&
        !!projectPermissions &&
        (projectPermissions.canCreateBuyerUnitChatThreads || projectPermissions.canCreateInternalUnitChatThreads);

    const handleCreateThreadClick = canCreateThreads
        ? () => {
              setShowCreateThreadModal(true);
          }
        : undefined;

    const handleFollowFilterChange = (v: FollowFilterType) => {
        setFollowFilterDropdownIsOpen(false);
        setFollowFilter(v);
    };

    const handleThreadTypeFilterChange = (x: 'All' | CommentThreadType) => {
        setTypeFilterDropdownIsOpen(false);
        setThreadTypeFilter(x);
    };

    const searchOptions = useMemo<CommentThreadListSearchOptions>(() => {
        const options: CommentThreadListSearchOptions = {
            profileId: profileId,
            projectId: projectId,
            residentialUnitId: residentialUnitId,
            threadTypes: internalMessageThreads,
            followState: followFilter === 'All' ? null : followFilter,
            isFlagged: showOnlyFlagged,
            nonEmpty: !residentialUnitId,
        };

        if (threadTypeFilter !== 'All') {
            options.threadTypes = [threadTypeFilter];
        }

        if (threadVisibilityFilter === 'Buyer') {
            options.includesBuyer = true;
        } else if (threadVisibilityFilter === 'Internal') {
            options.includesBuyer = false;
        }

        return options;
    }, [residentialUnitId, profileId, projectId, followFilter, threadTypeFilter, threadVisibilityFilter, showOnlyFlagged]);

    function renderSelectedThread() {
        if (isMobile) {
            return null;
        }

        if (!selectedThreadId) {
            return <NoThreadPlaceholder />;
        }

        return <CommentThreadWrapper threadId={selectedThreadId!} />;
    }

    return (
        <>
            <ButtonBar className={theme.buttonBar} margins='bottom'>
                <DropdownButton
                    variant="tertiary"
                    className={theme.threadTypeDropDown}
                    icon={faChevronDown}
                    label={
                        <span className={theme.threadTypeMainText}>
                            {threadTypeFilter === 'All' && <Trans>All</Trans>}
                            {threadTypeFilter === CommentThreadType.Project && <Trans>Project threads</Trans>}
                            {threadTypeFilter === CommentThreadType.ResidentialUnit && <Trans>Unit threads</Trans>}
                            {threadTypeFilter === CommentThreadType.ChangeRequest && <Trans>Change request threads</Trans>}
                            {threadTypeFilter === CommentThreadType.ExternalAgreement && (
                                <Trans>External agreement threads</Trans>
                            )}
                        </span>
                    }>
                    <Dropdown.Item onClick={() => handleThreadTypeFilterChange('All')}>
                        <Trans>All</Trans>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleThreadTypeFilterChange(CommentThreadType.Project)}>
                        <Trans>Project threads</Trans>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleThreadTypeFilterChange(CommentThreadType.ResidentialUnit)}>
                        <Trans>Unit threads</Trans>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleThreadTypeFilterChange(CommentThreadType.ChangeRequest)}>
                        <Trans>Change request threads</Trans>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleThreadTypeFilterChange(CommentThreadType.ExternalAgreement)}>
                        <Trans>External agreement threads</Trans>
                    </Dropdown.Item>
                </DropdownButton>

                <Tabs
                    activeId={threadVisibilityFilter}
                    onChange={(id) => setThreadVisibilityFilter(id as 'All' | 'Buyer' | 'Internal')}
                    variant="pill"
                    size="sm">
                    <Tabs.Tab id="All" label={<Trans>All</Trans>} />
                    <Tabs.Tab
                        id="Buyer"
                        label={<Trans>Includes Buyers</Trans>}
                        disabled={threadTypeFilter === CommentThreadType.ExternalAgreement}
                    />
                    <Tabs.Tab id="Internal" label={<Trans>Internal</Trans>} />
                </Tabs>

                <ButtonBar.Right className={theme.actions}>
                    <SlideToggle checked={showOnlyFlagged} onChange={(e) => setShowOnlyFlagged(e.target.checked)}>
                        <Trans>Show only flagged threads</Trans>
                    </SlideToggle>
                    <DropdownButton
                        variant="tertiary"
                        icon={faChevronDown}
                        label={
                            <span className={theme.dropDownMainText}>
                                {followFilter === 'All' && <Trans>All threads</Trans>}
                                {followFilter === 'NotFollowed' && (
                                    <>
                                        <FontAwesomeIcon icon={faEyeSlash} />
                                        <Trans>Not followed threads</Trans>
                                    </>
                                )}
                                {followFilter === 'Followed' && (
                                    <>
                                        <FontAwesomeIcon icon={faEye} />
                                        <Trans>Followed threads</Trans>
                                    </>
                                )}
                            </span>
                        }>
                        <Dropdown.Item onClick={() => handleFollowFilterChange('All')}>
                            <Trans>All threads</Trans>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleFollowFilterChange('NotFollowed')} icon={faEyeSlash}>
                            <Trans>Not followed threads</Trans>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleFollowFilterChange('Followed')} icon={faEye}>
                            <Trans>Followed threads</Trans>
                        </Dropdown.Item>
                    </DropdownButton>
                    {handleCreateThreadClick && (
                        <Button variant="tertiary" onClick={handleCreateThreadClick} icon={faPlusCircle}>
                            <Trans>Create new thread</Trans>
                        </Button>
                    )}
                    {handleSendNewMultiMessageClick && (
                        <ProjectPermissionToggle condition={'canPostOnMainBuyerChat'}>
                            <Button variant="tertiary" onClick={handleSendNewMultiMessageClick} icon={faPlusCircle}>
                                <Trans>Send a message to several units</Trans>
                            </Button>
                        </ProjectPermissionToggle>
                    )}
                </ButtonBar.Right>
            </ButtonBar>

            <div className={theme.container}>
                <div className={theme.threads}>
                    <CommentThreadListSearchable
                        onSelectedThreadGone={() => setSelectedThread(null)}
                        hrefGenerator={(thread) => replaceParams({ id: thread.threadId?.toString() ?? undefined })}
                        selectedThreadId={selectedThreadId}
                        searchOptions={searchOptions}
                        ref={threadListRef}
                        showBadges
                        // disablePagination={canCreateThreads}
                    />
                </div>
                {renderSelectedThread()}
            </div>

            {canCreateThreads && (
                <CreateCommentThreadModal
                    onCreate={handleNewThreadCreated}
                    residentialUnitId={residentialUnitId!}
                    isOpen={showCreateThreadModal}
                    toggle={() => setShowCreateThreadModal(false)}
                />
            )}

            {!residentialUnitId && (
                <SendResidentialUnitMessageModal
                    toggle={() => setSendMultiMessageModalIsOpen(false)}
                    isOpen={sendMultiMessageModalIsOpen}
                    projectId={projectId}
                />
            )}
        </>
    );
};

export default CommentsFilterable;
